<template>
  <d-modal
    :active="isActive"
    :width="600"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-2xl font-bold">
          {{ user ? 'Edit User' : 'Add New User' }}
        </h3>
        <p>
          {{ user ? 'Update user information and permissions' : 'Create a new user account' }}
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Active Toggle -->
        <div class="form-control">
          <d-switch v-model="form.active">
            Active User
          </d-switch>
        </div>

        <!-- Name Fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <d-field label="First Name *">
            <d-input 
              v-model="form.firstName" 
              :required="true"
              placeholder="Enter first name"
            />
          </d-field>
          
          <d-field label="Last Name *">
            <d-input 
              v-model="form.lastName" 
              :required="true"
              placeholder="Enter last name"
            />
          </d-field>
        </div>

        <!-- Email Field -->
        <d-field label="Email Address *">
          <d-input 
            v-model="form.email" 
            type="email"
            :required="true"
            placeholder="Enter email address"
          />
        </d-field>

        <!-- Password Field -->
        <d-field :label="user ? 'New Password (leave blank to keep current)' : 'Password *'">
          <d-input 
            v-model="form.password" 
            type="password"
            :required="!user"
            placeholder="Enter password"
          />
        </d-field>

        <!-- Phone Number -->
        <d-field label="Phone Number">
          <d-input 
            v-model="form.phoneNumber" 
            placeholder="(555) 123-4567"
            @input="formatPhoneNumber"
          />
        </d-field>

        <!-- Role Selection -->
        <d-field label="User Role *">
          <d-select 
            v-model="form.roleId" 
            :required="true"
            placeholder="Select a role"
          >
            <option value="">Select a role</option>
            <option 
              v-for="role in roles" 
              :key="role.id" 
              :value="role.id"
            >
              {{ role.name }}
            </option>
          </d-select>
        </d-field>

        <!-- Subject Areas for Area Admin -->
        <div v-if="selectedRole && selectedRole.name === 'Area Admin'" class="space-y-4">
          <d-field label="Managed Subject Areas">
            <d-select 
              v-model="selectedManagedSubjectAreaId"
              placeholder="Add a subject area"
            >
              <option value="">Add a subject area</option>
              <option 
                v-for="subjectArea in availableManagedSubjectAreas" 
                :key="subjectArea.id" 
                :value="subjectArea.id"
              >
                {{ subjectArea.name }}
              </option>
            </d-select>
            
            <!-- Selected Subject Areas -->
            <div v-if="selectedManagedSubjectAreas.length > 0" class="mt-3 flex flex-wrap gap-2">
              <d-chip 
                v-for="subjectArea in selectedManagedSubjectAreas" 
                :key="subjectArea.id"
                color="primary"
                size="sm"
                label
                closable
                class="mr-2 mb-3"
                @close="removeManagedSubjectArea(subjectArea.id)"
              >
                {{ subjectArea.name }}
              </d-chip>
            </div>
          </d-field>
        </div>

        <!-- Subject Areas for Registrant -->
        <div v-if="selectedRole && selectedRole.name === 'Registrant'" class="space-y-4">
          <div>To manage what subject areas a registrant can access, utilize the access option for emails or domains within the subject area listing screen</div>
          <div v-if="emailVerificationEnabled" class="form-control">
            <d-switch v-model="form.emailConfirmed">
              Email Confirmed
            </d-switch>
          </div>
          <d-field label="Accessible Subject Areas">
            <!-- Selected Subject Areas -->
            <div v-if="selectedUsedSubjectAreas.length > 0" class="mt-3 flex flex-wrap gap-2">
              <d-chip 
                v-for="subjectArea in selectedUsedSubjectAreas" 
                :key="subjectArea.id"
                color="secondary"
                size="sm"
                label
                class="mr-2 mb-3"
              >
                {{ subjectArea.name }}
              </d-chip>
            </div>
          </d-field>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-3 pt-6 border-t">
          <d-btn 
            flat
            @click="handleClose"
            :disabled="saving || syncing"
          >
            Cancel
          </d-btn>
          <d-btn 
            v-if="user"
            color="secondary"
            @click="handleSyncAccess"
            :loading="syncing"
            :disabled="saving || syncing"
          >
            Sync Access
          </d-btn>
          <d-btn 
            type="submit" 
            color="primary"
            :loading="saving"
            :disabled="saving || syncing"
          >
            {{ user ? 'Update User' : 'Create User' }}
          </d-btn>
        </div>
      </form>
    </div>
  </d-modal>
</template>

<script>
import axios from 'axios';
import { showErrorToast, toast } from '@services/toast.service';

export default {
  name: 'UserModal',
  props: {
    user: {
      type: Object,
      default: null,
    },
    active: {
      type: Boolean,
      default: false,
    },
    emailVerificationEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'saved'],
  
  data() {
    return {
      isActive: false,
      saving: false,
      syncing: false,
      loading: false,
      roles: [],
      subjectAreas: [],
      selectedManagedSubjectAreaId: '',
      selectedUsedSubjectAreaId: '',
      form: {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phoneNumber: '',
        emailConfirmed: false,
        roleId: '',
        managedSubjectAreas: [],
        usedSubjectAreas: [],
        active: true,
      },
    };
  },

  computed: {
    selectedRole() {
      return this.roles.find(role => role.id === this.form.roleId);
    },

    selectedManagedSubjectAreas() {
      return this.subjectAreas.filter(sa => 
        this.form.managedSubjectAreas.includes(sa.id)
      );
    },

    selectedUsedSubjectAreas() {
      return this.subjectAreas.filter(sa => 
        this.form.usedSubjectAreas.includes(sa.id)
      );
    },

    availableManagedSubjectAreas() {
      return this.subjectAreas.filter(sa => 
        !this.form.managedSubjectAreas.includes(sa.id)
      );
    },

    availableUsedSubjectAreas() {
      return this.subjectAreas.filter(sa => 
        !this.form.usedSubjectAreas.includes(sa.id)
      );
    },

    isFormValid() {
      return (
        this.form.firstName.trim() &&
        this.form.lastName.trim() &&
        this.form.email.trim() &&
        this.form.roleId &&
        (this.user || this.form.password.trim())
      );
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(newValue) {
        this.isActive = newValue;
        if (newValue) {
          this.initializeModal();
        }
      },
    },

    selectedManagedSubjectAreaId(newVal) {
      if (newVal) {
        this.addToManagedSubjectAreas(newVal);
        this.selectedManagedSubjectAreaId = '';
      }
    },

    selectedUsedSubjectAreaId(newVal) {
      if (newVal) {
        this.addToUsedSubjectAreas(newVal);
        this.selectedUsedSubjectAreaId = '';
      }
    },
  },

  methods: {
    async initializeModal() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadRoles(),
          this.loadSubjectAreas(),
        ]);
        
        if (this.user) {
          this.loadUserData();
        } else {
          this.resetForm();
        }
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.loading = false;
      }
    },

    async loadRoles() {
      const { data } = await axios.get('/api/users/roles');
      this.roles = data;
    },

    async loadSubjectAreas() {
      const { data } = await axios.get('/api/subject-areas');
      this.subjectAreas = data;
    },

    loadUserData() {
      if (!this.user) return;

      this.form = {
        id: this.user.id,
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        email: this.user.email || '',
        password: '',
        phoneNumber: this.user.phoneNumber || '',
        emailConfirmed: this.user.emailConfirmed ?? false,
        roleId: this.user.userRoles?.[0]?.id || '',
        managedSubjectAreas: this.user.managedSubjectAreas?.map(sa => sa.id) || [],
        usedSubjectAreas: this.user.usedSubjectAreas?.map(sa => sa.id) || [],
        active: this.user.active ?? true,
      };
      
      this.formatPhoneNumber();
    },

    resetForm() {
      this.form = {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phoneNumber: '',
        emailConfirmed: false,
        roleId: '',
        managedSubjectAreas: [],
        usedSubjectAreas: [],
        active: true,
      };
      this.selectedManagedSubjectAreaId = '';
      this.selectedUsedSubjectAreaId = '';
    },

    formatPhoneNumber() {
      if (!this.form.phoneNumber) return;
      
      const cleaned = this.form.phoneNumber.replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      
      if (match) {
        this.form.phoneNumber = `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    },

    addToManagedSubjectAreas(subjectAreaId) {
      if (subjectAreaId && !this.form.managedSubjectAreas.includes(subjectAreaId)) {
        this.form.managedSubjectAreas.push(subjectAreaId);
      }
    },

    addToUsedSubjectAreas(subjectAreaId) {
      if (subjectAreaId && !this.form.usedSubjectAreas.includes(subjectAreaId)) {
        this.form.usedSubjectAreas.push(subjectAreaId);
      }
    },

    removeManagedSubjectArea(id) {
      this.form.managedSubjectAreas = this.form.managedSubjectAreas.filter(
        saId => saId !== id
      );
    },

    removeUsedSubjectArea(id) {
      this.form.usedSubjectAreas = this.form.usedSubjectAreas.filter(
        saId => saId !== id
      );
    },

    async handleSubmit() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        let response;
        if (this.user) {
          response = await axios.patch(`/api/users/${this.user.id}`, this.form);
        } else {
          response = await axios.post('/api/users', this.form);
        }

        this.$emit('saved', response.data);
        this.handleClose();
        
        // Show success message
        const message = this.user ? 'User updated successfully' : 'User created successfully';
        toast.success(message);
        
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.saving = false;
      }
    },

    async handleSyncAccess() {
      if (!this.user) return;

      this.syncing = true;
      try {
        await axios.post(`/api/users/${this.user.id}/sync-access`);
        
        toast.success('User access synchronized successfully');
        
        // Reload user data to show updated subject areas
        const { data } = await axios.get(`/api/users/${this.user.id}`);
        this.$emit('saved', data);
        this.loadUserData();
        
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.syncing = false;
      }
    },

    handleClose() {
      this.isActive = false;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Removed duplicate modal styling to fix ghost outline issue */
</style>
